// ═══════════════════════════════════════════════════════════════
//   V19.5 — Team Page (admin-only)
// ═══════════════════════════════════════════════════════════════
//
// Admin manages which staff get access to which clients. The page
// shows two side-by-side panels:
//   LEFT  — list of clients (each row has Partner select + Assigned
//           staff multi-select).
//   RIGHT — directory of users known to the CPA app + portal users
//           (proxied via /api/team/portal-users in production).
//
// Saves are per-client, applied via PUT /api/team/clients/:id/assignments.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save, Users, Shield } from 'lucide-react';

const COLORS = {
  primary: '#0085CA',
  dark: '#1A1D21',
  muted: '#6B7280',
  card: '#FFFFFF',
  border: '#E5E7EB',
  off: '#F4F6F8',
  good: '#10B981',
  warn: '#F59E0B',
};

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'admin') return;
    const token = localStorage.getItem('smj_token') || localStorage.getItem('token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/api/clients`, auth),
      axios.get(`${API}/api/team/users`, auth),
      axios.get(`${API}/api/team/portal-users`, auth).catch(() => ({ data: { users: [], source: 'unavailable' } })),
    ]).then(([cRes, uRes, pRes]) => {
      setClients(cRes.data || []);
      setUsers(uRes.data?.users || []);
      setPortalUsers(pRes.data?.users || []);
    }).catch(err => {
      console.error('Team load error', err);
    });
  }, [loading, user]);

  const directory = useMemo(() => {
    // Merge local users + portal users by email. Portal entries take
    // precedence (newer name/role/status). The id field captures the
    // preferred lookup key; a parallel id_aliases array carries any
    // additional IDs the same person might be known by (e.g., a legacy
    // local-DB id when their portal_user_id is different — common in
    // preview/dev). The client-assignment picker matches against
    // id OR any alias.
    const byEmail = new Map();
    users.forEach(u => {
      const ids = [u.id, u.portal_user_id].filter(Boolean);
      byEmail.set((u.email || '').toLowerCase(), {
        id: u.portal_user_id || u.id,
        id_aliases: ids,
        email: u.email,
        name: u.name,
        role: u.role,
        source: 'local',
      });
    });
    portalUsers.forEach(u => {
      const existing = byEmail.get((u.email || '').toLowerCase());
      const aliases = new Set(existing?.id_aliases || []);
      aliases.add(u.id);
      byEmail.set((u.email || '').toLowerCase(), {
        id: u.id,
        id_aliases: Array.from(aliases),
        email: u.email,
        name: u.name,
        role: u.role === 'admin' ? 'admin' : 'staff',
        source: 'portal',
      });
    });
    return Array.from(byEmail.values())
      .filter(u => (u.email || '').toLowerCase().endsWith('@steelemartin.com'))
      .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
  }, [users, portalUsers]);

  // Resolve a stored partner_user_id (or assigned_staff entry) to a
  // directory user. Tries the canonical id first then any alias.
  const resolveDirEntry = (uid) => {
    if (!uid) return null;
    return directory.find(u => u.id === uid || (u.id_aliases || []).includes(uid)) || null;
  };

  const saveClient = async (clientId, payload) => {
    if (busy) return;
    setBusy(true);
    setSaveStatus(s => ({ ...s, [clientId]: 'saving' }));
    try {
      const token = localStorage.getItem('smj_token') || localStorage.getItem('token');
      const resp = await axios.put(
        `${API}/api/team/clients/${clientId}/assignments`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Replace the client in our local state so the UI reflects the
      // canonical server value immediately (no stale partner picker).
      setClients(prev => prev.map(c => c.id === clientId ? resp.data.client : c));
      setSaveStatus(s => ({ ...s, [clientId]: 'saved' }));
      setTimeout(() => setSaveStatus(s => ({ ...s, [clientId]: null })), 1500);
    } catch (err) {
      setSaveStatus(s => ({ ...s, [clientId]: 'error' }));
      // eslint-disable-next-line no-alert
      alert(`Save failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (!user || user.role !== 'admin') {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '60px auto' }}>
        <Shield size={48} color={COLORS.muted} />
        <h1 style={{ marginTop: 16, fontSize: 24, fontWeight: 700, color: COLORS.dark }}>
          Admin access required
        </h1>
        <p style={{ marginTop: 8, color: COLORS.muted }}>
          The Team page is admin-only.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          data-testid="team-page-back-btn"
          style={{
            marginTop: 16, padding: '10px 16px', borderRadius: 8,
            border: 'none', background: COLORS.primary, color: '#fff',
            cursor: 'pointer', fontWeight: 600,
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div data-testid="team-page" style={{ minHeight: '100vh', background: COLORS.off }}>
      <div style={{
        background: COLORS.card,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}>
        <button
          onClick={() => navigate('/dashboard')}
          data-testid="team-page-back-btn"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${COLORS.border}`, background: '#fff',
            color: COLORS.dark, cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}
        >
          <ArrowLeft size={16} /> Dashboard
        </button>
        <div>
          <h1 className="font-heading" style={{ fontSize: 22, fontWeight: 700, color: COLORS.dark, margin: 0 }}>
            Team
          </h1>
          <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
            Assign partners and staff to each client engagement.
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.7fr 1fr',
        gap: 24,
        padding: 32,
        maxWidth: 1400,
        margin: '0 auto',
      }}>
        {/* CLIENTS PANEL */}
        <div data-testid="team-clients-panel" style={{
          background: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${COLORS.border}`,
            fontSize: 13, fontWeight: 700, color: COLORS.dark,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Users size={16} /> Client Assignments ({clients.length})
          </div>
          {clients.length === 0 ? (
            <div style={{ padding: 24, color: COLORS.muted, fontSize: 13 }}>
              No clients yet.
            </div>
          ) : (
            <div>
              {clients.map(c => (
                <ClientAssignmentRow
                  key={c.id}
                  client={c}
                  directory={directory}
                  status={saveStatus[c.id]}
                  onSave={(payload) => saveClient(c.id, payload)}
                />
              ))}
            </div>
          )}
        </div>

        {/* DIRECTORY PANEL */}
        <div data-testid="team-directory-panel" style={{
          background: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${COLORS.border}`,
            fontSize: 13, fontWeight: 700, color: COLORS.dark,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Shield size={16} /> Firm Directory ({directory.length})
          </div>
          {directory.length === 0 ? (
            <div style={{ padding: 24, color: COLORS.muted, fontSize: 12 }}>
              No portal users found yet. Employees show up here automatically
              after their first sign-in via the portal.
            </div>
          ) : (
            <div>
              {directory.map(u => (
                <div
                  key={u.id}
                  data-testid={`directory-row-${u.email}`}
                  style={{
                    padding: '12px 20px',
                    borderBottom: `1px solid ${COLORS.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark }}>
                    {u.name || u.email}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.muted }}>
                    {u.email} · <span style={{
                      color: u.role === 'admin' ? COLORS.primary : COLORS.muted,
                      fontWeight: u.role === 'admin' ? 600 : 400,
                    }}>{u.role}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientAssignmentRow({ client, directory, status, onSave }) {
  const [partnerId, setPartnerId] = useState(client.partner_user_id || '');
  const [assigned, setAssigned] = useState(client.assigned_staff || []);

  // Sync local edits when the parent updates this client after save.
  useEffect(() => {
    setPartnerId(client.partner_user_id || '');
    setAssigned(client.assigned_staff || []);
  }, [client.partner_user_id, client.assigned_staff]);

  const dirty = (partnerId || '') !== (client.partner_user_id || '')
    || JSON.stringify([...(assigned || [])].sort()) !== JSON.stringify([...(client.assigned_staff || [])].sort());

  const partnerLookup = directory.find(u => u.id === partnerId);

  const submit = () => {
    if (!dirty) return;
    const payload = {
      partner_user_id: partnerId || null,
      partner_email: partnerLookup?.email || null,
      partner_name: partnerLookup?.name || null,
      assigned_staff: assigned,
    };
    onSave(payload);
  };

  const toggleAssigned = (uid) => {
    setAssigned(prev => prev.includes(uid)
      ? prev.filter(x => x !== uid)
      : [...prev, uid]);
  };

  return (
    <div style={{
      padding: '16px 20px',
      borderBottom: `1px solid ${COLORS.border}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.dark }}>
            {client.company}
          </div>
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>
            {client.engagement_type || 'preparation'} · {client.framework || 'none'}
            {client.is_firm_self ? ' · firm self-engagement' : ''}
          </div>
        </div>
        <button
          onClick={submit}
          disabled={!dirty || status === 'saving'}
          data-testid={`team-save-${client.id}`}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            background: dirty ? COLORS.primary : COLORS.border,
            color: dirty ? '#fff' : COLORS.muted,
            cursor: dirty ? 'pointer' : 'not-allowed',
            fontSize: 12,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Save size={14} />
          {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 6 }}>
            Partner
          </div>
          <select
            value={partnerId}
            data-testid={`partner-select-${client.id}`}
            onChange={(e) => setPartnerId(e.target.value)}
            style={{
              width: '100%', padding: '7px 9px', borderRadius: 6,
              border: `1px solid ${COLORS.border}`, fontSize: 12, background: '#fff',
            }}
          >
            <option value="">— None —</option>
            {directory.map(u => (
              <option key={u.id} value={u.id}>{u.name || u.email}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 6 }}>
            Assigned Staff ({assigned.length})
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 90, overflowY: 'auto',
            padding: 6, border: `1px solid ${COLORS.border}`, borderRadius: 6, background: '#FAFBFC',
          }}>
            {directory.length === 0 ? (
              <span style={{ fontSize: 11, color: COLORS.muted }}>No directory users available.</span>
            ) : directory.map(u => {
              const on = assigned.includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggleAssigned(u.id)}
                  data-testid={`assign-${client.id}-${u.email}`}
                  style={{
                    fontSize: 11,
                    padding: '4px 9px',
                    borderRadius: 999,
                    border: `1px solid ${on ? COLORS.primary : COLORS.border}`,
                    background: on ? COLORS.primary : '#fff',
                    color: on ? '#fff' : COLORS.dark,
                    cursor: 'pointer',
                    fontWeight: on ? 600 : 500,
                  }}
                >
                  {u.name || u.email}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
